import React, { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { deleteBoard } from '../api/boardApi';
import { useBoardStore } from '../store/useBoardStore';
import { usePersonStore } from 'stores';
import { useGeminiLanguage } from '../hooks/useGeminiLanguage';
import { getCanvasMessages } from '../utils/canvasI18n';
import BoardContextMemorySection from './BoardContextMemorySection';
import DeleteCanvasConfirmDialog from '../../components/DeleteCanvasConfirmDialog';

/**
 * Canvas settings — open from the title row.
 * Owner can permanently delete the board.
 */
export default function BoardSettingsPanel({
  personName: personNameProp,
  hashtag: hashtagProp,
  isOwner: isOwnerProp,
}) {
  const navigate = useNavigate();
  const params = useParams();
  const user = useSelector(state => state.user);
  const { geminiLanguage } = useGeminiLanguage();
  const messages = getCanvasMessages(geminiLanguage);
  const removeBoardLocal = usePersonStore(state => state.removeBoardLocal);
  const clearBoard = useBoardStore(state => state.clearBoard);
  const storeHashtag = useBoardStore(state => state.hashtag);
  const storeIsOwner = useBoardStore(state => state.isOwner);
  const canEdit = useBoardStore(state => state.canEdit);

  const hashtag = hashtagProp || storeHashtag || params.keyword || '';
  const personName = personNameProp
    || (params.person_name ? decodeURIComponent(params.person_name) : '')
    || '';
  const isOwner = isOwnerProp ?? storeIsOwner;

  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const canDelete =
    isOwner
    && Boolean(hashtag)
    && String(hashtag).toLowerCase() !== 'no_hashtag';

  const canOpenSettings = Boolean(isOwner || canEdit) && Boolean(hashtag);

  const handleClose = useCallback(() => {
    if (busy) return;
    setOpen(false);
    setConfirmOpen(false);
    setError('');
  }, [busy]);

  const handleDelete = useCallback(async () => {
    if (!canDelete || busy) return;
    setBusy(true);
    setError('');
    try {
      await deleteBoard(hashtag);
      removeBoardLocal?.(hashtag);
      clearBoard?.();
      setConfirmOpen(false);
      setOpen(false);
      const username = personName || user?.username;
      if (username) {
        navigate(`/${encodeURIComponent(username)}`);
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err?.message || messages.deleteCanvasFailed);
      setBusy(false);
    }
  }, [
    canDelete,
    busy,
    hashtag,
    removeBoardLocal,
    clearBoard,
    personName,
    user?.username,
    navigate,
    messages.deleteCanvasFailed,
  ]);

  if (!canOpenSettings) return null;

  return (
    <>
      <Tooltip title={messages.canvasSettings} arrow placement="top">
        <IconButton
          size="small"
          className="board-canvas-settings-btn"
          onClick={() => setOpen(true)}
          aria-label={messages.canvasSettings}
        >
          <SettingsOutlinedIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{messages.canvasSettings}</DialogTitle>
        <DialogContent>
          {error && !confirmOpen ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            #{hashtag}
          </Typography>

          <BoardContextMemorySection
            isOwner={isOwner}
            busy={busy}
            messages={messages}
            onError={setError}
          />

          {isOwner ? (
            <Box
              sx={{
                mt: 1,
                p: 2,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'error.light',
                bgcolor: 'rgba(244, 63, 94, 0.04)',
              }}
            >
              <Typography variant="subtitle2" color="error" sx={{ mb: 0.5 }}>
                {messages.deleteCanvasTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {messages.deleteCanvasDescription}
              </Typography>
              {canDelete ? (
                <Button
                  color="error"
                  variant="outlined"
                  size="small"
                  disabled={busy}
                  onClick={() => {
                    setError('');
                    setConfirmOpen(true);
                  }}
                >
                  {messages.deleteCanvasButton}
                </Button>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {messages.deleteCanvasDefaultBlocked}
                </Typography>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={busy}>
            {messages.close}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteCanvasConfirmDialog
        open={confirmOpen}
        hashtag={hashtag}
        busy={busy}
        error={error}
        onClose={() => {
          if (!busy) {
            setConfirmOpen(false);
            setError('');
          }
        }}
        onConfirm={handleDelete}
        copy={{
          title: messages.deleteCanvasConfirmTitle,
          hint: messages.deleteCanvasConfirmHint,
          label: messages.deleteCanvasConfirmLabel,
          cancel: messages.cancel,
          confirm: messages.deleteCanvasForever,
          deleting: messages.deletingCanvas,
        }}
      />
    </>
  );
}
